<script>
    import _ from 'lodash'
    import $ from './utils/helper'
    import moment from 'moment'
    import numeral from 'numeral'
    import Mousetrap from 'mousetrap'
    import appConfig from './app.config'
    import { authComputed, appComputed, apiComputed } from '@state/helpers'
    import CONSTANTS from './constants'
    import ChangeLog from './components/change-log.vue'
    import MessageLog from './components/message-log.vue'
    import ButtonSelect from './components/button-select.vue'
    import PasswordDialog from './components/password-dialog.vue'
    import LoadingIndicator from './components/loading-indicator.vue'
    import CoachingCalendar from './components/coaching-calendar'
    import LinkFooter from './components/link-footer'
    import UserSettingsDialog from '@components/user-settings-dialog'
    import CoachingTeaser from '@components/coaching-teaser'

    export default {
        components: {
            CoachingTeaser,
            UserSettingsDialog,
            LinkFooter,
            CoachingCalendar,
            LoadingIndicator,
            ChangeLog,
            MessageLog,
            ButtonSelect,
            PasswordDialog
        },
        data() {
            return {
                title: `${appConfig.title}`,
                author: `${appConfig.author}`,
                version: `${appConfig.version}`,
                dark: null,
                drawer: false,
                drawerMessages: false,
                drawerCalendar: false,
                right: false,
                left: false,
                menu: false,
                message: true,
                hints: false,
                hasChanges: false,
                hintChanges: null,
                lang: 'de',
                locales: [],
                pwdialog: false,
                intervalId: null,
                visibilityHandler: null,
                messagesHint: false,
                autoReload: true,
                hidden: false,
                formattypes: [],
                formattype: 'iso',
                showSettings: false,
                showTutorialDialog: false,
                tutorials: [
                    {
                        label: 'Übersicht',
                        href: 'https://youtu.be/au2bnDrj2Zo'
                    },
                    {
                        label: 'Organisationen',
                        href: 'https://youtu.be/gMXa_rxy-HI'
                    },
                    {
                        label: 'Mitarbeiter',
                        href: 'https://youtu.be/94BBxf5G_cc'
                    },
                    {
                        label: 'Vorlagen',
                        href: 'https://youtu.be/gPZayhL0EzY'
                    },
                    {
                        label: 'Coachings',
                        href: 'https://youtu.be/Ag-SO6GVsSM'
                    },
                    {
                        label: 'Reporting',
                        href: 'https://youtu.be/6wgrfs04aS8'
                    }
                ]
            }
        },

        computed: {
            ...authComputed,
            ...appComputed,
            ...apiComputed,
            currentYear() {
                return moment().format('YYYY')
            },
            userInvitations() {
                return this.$store.getters['api/userInvitations']
            },
            organisations() {
                return this.$store.getters['api/organisations']
            },
            routes() {
                return [
                    {
                        to: '/home',
                        id: 'home',
                        label: this.$vuetify.lang.t('$vuetify.routes.home'),
                        icon: 'mdi-home-account'
                    },
                    {
                        to: '/organizations',
                        id: 'organizations',
                        label: this.$vuetify.lang.t('$vuetify.routes.organizations'),
                        icon: 'mdi-office-building'
                    },
                    {
                        to: '/employees',
                        id: 'employees',
                        label: this.$vuetify.lang.t('$vuetify.routes.staff'),
                        icon: 'mdi-face-agent',
                        requires: ['organisation'],
                        groups: ['Administrator', 'Coach']
                    },
                    {
                        to: '/templates',
                        id: 'templates',
                        label: this.$vuetify.lang.t('$vuetify.routes.templates'),
                        icon: 'mdi-ballot-outline',
                        requires: ['organisation'],
                        groups: ['Administrator', 'Coach']
                    },
                    {
                        to: '/coachings',
                        id: 'coachings',
                        label: this.$vuetify.lang.t('$vuetify.routes.coaching'),
                        icon: 'mdi-account-group',
                        requires: ['organisation']
                    },
                    {
                        to: '/reporting',
                        id: 'reporting',
                        label: this.$vuetify.lang.t('$vuetify.routes.reporting'),
                        icon: 'mdi-file-chart',
                        requires: ['organisation']
                    }
                ]
            },
            availableRoutes() {
                const hasOrg = _.get(this.organisation, 'organisationId')
                return this.routes.filter(route => {
                    const hasValidOrg = !route.requires || (route.requires.includes('organisation') && !!hasOrg)
                    const hasValidGroup = !route.groups || route.groups.includes(this.userGroup)
                    return hasValidOrg && hasValidGroup
                })
            },
            loadingScreen() {
                return this.fetching.disabled
            },
            fetchingChange() {
                return this.fetching.change
            },
            appClass() {
                const route = `app-route-${this.$route.name}`
                const bp = `app-bp-${this.$vuetify.breakpoint.name}`
                const fetching = this.fetchingChange ? 'is--fetching' : ''
                return `${route} ${bp} ${fetching}`.trim()
            },
            currentUserName() {
                return _.get(this.currentUser, 'user.username')
            },
            currentUserEmail() {
                return _.get(this.currentUser, 'user.email')
            },
            currentUserGroup() {
                return this.userGroup ? this.$vuetify.lang.t(`$vuetify.groups.${this.userGroup}`) : ''
            },
            currentUserMenuIcon() {
                switch (this.userGroup) {
                    case CONSTANTS.GROUP_ADMIN:
                        return 'mdi-account-star'
                    case CONSTANTS.GROUP_COACH:
                        return 'mdi-account-circle'
                    default:
                        return 'mdi-account-lock'
                }
            },
            // get labels translation for tour
            tourOptions() {
                const lang = this.$vuetify.lang.current
                const labels = this.$vuetify.lang.locales[lang].tour.labels
                return {labels, enableScrolling: false}
            },
            formatMessagesCount() {
                return $.formatCount(this.messagesCount)
            },
            breakpointListener() {
                return this.$vuetify.breakpoint.name
            },
            showLoginRoute() {
                const route = this.$route.name
                return !this.loggedIn && !['login', 'resetpassword'].includes(route)
            },
            showRegisterRoute() {
                const route = this.$route.name
                return !this.loggedIn && !['landing', 'verifyemail', 'resetpassword'].includes(route)
            },
            showLanguageSelection() {
                const route = this.$route.name
                return !this.loggedIn && !['verifyemail', 'resetpassword'].includes(route)
            },
            changelogs() {
                return this.$store.getters['app/changelogs']
            },
            menuMaxHeight() {
                const {height} = this.$vuetify.breakpoint
                return height - 24 // menu: top = 12px
            },
            drawerWidth() {
                const {width} = this.$vuetify.breakpoint
                return width < 640 ? 400 : 640
            }
        },
        watch: {
            // reload on change to route "home"
            $route() {
                if (this.$route.name === 'home') this.reload()
            },
            breakpointListener() {
                this.$store.dispatch('app/breakpoint', this.$vuetify.breakpoint)
                // NOTE do not save denseMode, if mobile view set denseMode
                // this.dense = bp === 'xs'
                this.$nextTick(() => {
                    if (this.$refs && this.$refs.tabs) this.$refs.tabs.callSlider()
                })
            },
            currentUser() {
                this.updateSettings()
            },
            async dark(dark) {
                this.$vuetify.theme.dark = dark
                const theme = dark ? 'Dark' : 'Light'
                if (this.currentUser && theme !== _.get(this.currentUser, 'settings.theme')) {
                    const settings = { userId: this.currentUser.user.userId, theme }
                    await this.$store.dispatch('api/USERS_SETTINGS', settings)
                }
                // NOTE SET_APP_THEME is needed, if user log out
                this.$store.commit('app/SET_APP_THEME', dark ? 'dark' : 'light')
            },
            dense(dense) {
                // NOTE do not save denseMode, if mobile view set denseMode
                if (this.currentUser && dense !== _.get(this.currentUser, 'settings.denseMode')) {
                    const settings = { userId: this.currentUser.user.userId, denseMode: dense }
                    this.$store.dispatch('api/USERS_SETTINGS', settings)
                }
            },
            async lang(lang) {
                this.$vuetify.lang.current = lang
                await this.$store.dispatch('app/language', lang)
                if (this.currentUser) {
                    const language = lang === 'de' ? 'German' : 'English'
                    if (language !== this.currentUser.settings.language) {
                        const settings = { userId: this.currentUser.user.userId, language }
                        await this.$store.dispatch('api/USERS_SETTINGS', settings)
                    }
                }
                // if not logged in, modify query parameter for current language
                else {
                    const query = lang === 'de' ? '' : `lang=${this.$route.query.lang}`
                    const url = `${this.$router.mode === 'hash' ? '/#' : ''}${this.$route.path}${query ? '?' : ''}${query}`
                    window.history.pushState({}, null, url)
                }
            },
            formattype(type) {
                this.$store.dispatch('app/formatter', type)
            },
            language(lang) {
                this.lang = lang
                this.translateConstants()
            },
            loggedIn(_loggedIn) {
                if (!_loggedIn) this.$router.push({name: 'login', query: {redirectFrom: this.$route.fullPath}})
                // start auto reload
                // refresh invitations and update message button
                else this.checkInvitations()
            },
            changelogs() {
                this.updateChangelogs()
            },
            async right() {
                if (!this.right && this.currentUser) {
                    const lastVersionChangeLogs = _.first(this.changelogs).version
                    const settings = {userId: this.currentUser.user.userId, lastVersionChangeLogs}
                    await this.$store.dispatch('api/USERS_SETTINGS', settings)
                    this.hasChanges = false
                    this.hintChanges = null
                }
            },
            organisation(org) {
                this.checkRoute()
                if (!_.isEmpty(org)) this.reload()
            },
            userInvitations() {
                this.updateMessages()
            },
            messages() {
                this.messagesHint = !_.isEmpty(this.messages)
            },
            // reload data after app becomes visible again
            hidden() {
                if (!this.hidden) this.reload()
            }
        },

        created() {
            this.translateConstants()
            this.visibilityHandler = this.$visibility.change((evt, hidden) => {
                this.$store.commit('app/SET_VISIBILITY', hidden)
                this.hidden = hidden
            })
            Mousetrap.bind('* u', () => {
                this.showSettings = !this.showSettings
            })
            Mousetrap.bind('* m', () => {
                this.drawer = !this.drawer
            })

            if (!this.developerMode) return

            // DEV Global Test Helper
            const demo = () => {
                const templates = this.$store.getters['api/templates']
                if (_.isEmpty(templates)) return console.warn('no templates')
                const activeUsers = this.$store.getters['api/organisationUsersActive']
                const activeEmployees = activeUsers.filter(employee => CONSTANTS.GROUPS_EMPLOYEE.includes(employee.organizationGroup))
                if (_.isEmpty(activeEmployees)) return console.warn('no active employees')
                const activeCoaches = activeUsers.filter(employee => CONSTANTS.GROUPS_EDIT.includes(employee.organizationGroup))
                const demoEmployee = _.sample(activeEmployees)
                const demoCoach = _.sample(activeCoaches)
                const validTemplates = templates.filter(template => template.initials !== 'INTERN')
                const demoTemplate = _.sample(validTemplates)
                const name = demoTemplate.name
                const organisationId = this.organisation.organisationId
                const isFinished = _.random(0, 6) > 1
                const config = $.templateToCoaching.call(this, demoTemplate)
                // set header values
                const username = _.find(_.flatten(config), {name: 'username'})
                username.value = demoCoach.username
                const employeeName = _.find(_.flatten(config), {name: 'employee'})
                employeeName.value = demoEmployee.username
                // randomize building blocks
                const coachingData = $.randomizeConfigValues({config})
                const payload = {name, organisationId, coachingData, isFinished}
                this.$store.dispatch('api/COACHING_CREATE', {userId: demoEmployee.userId, ...payload})
                return true
            }
            let demoIntervalId
            window.coaching = {
                demoStart: ({timeout = 30000} = {}) => {
                    const demoAvailable = demo()
                    if (demoAvailable !== true) return 'coaching creation not possible'
                    demoIntervalId = window.setInterval(() => {
                        demo()
                    }, timeout)
                    return 'coaching creation started'
                },
                demoStop: () => {
                    if (demoIntervalId) window.clearInterval(demoIntervalId)
                    demoIntervalId = null
                    return 'coaching creation stopped'
                },
                tabindex: () => {
                    const nodes = document.querySelectorAll('[tabindex]')
                    _.each(nodes, node => {
                        if (node.tabIndex > 0) console.log('node.tagName', node.tagName, node.tabIndex)
                    })
                },
                debug: () => {
                    this.$json.open({
                        currentUser: this.currentUser,
                        coachings: this.$store.getters['api/coachings'],
                        templates: this.$store.getters['api/templates'],
                        organizations: this.$store.getters['api/organisations'],
                        organizationUsers: this.$store.getters['api/organisationUsers']
                    })
                },
                testDate: date => {
                    console.log('')
                    console.log('date', date)
                    console.log('moment()', moment(date).format())
                    console.log('moment().format', moment(date).format('YYYY-MM-DD HH:mm'))
                    console.log('moment().local().format', moment(date).local().format('YYYY-MM-DD HH:mm'))
                    console.log('moment.utc().local().format', moment.utc(date).local().format('YYYY-MM-DD HH:mm'))
                    console.log('moment.utc().format', moment.utc(date).format())
                    console.log('')
                }
            }

            Mousetrap.bind('* j', () => {
                window.coaching.debug()
            })
        },
        mounted() {
            const locales = Object.keys(this.$vuetify.lang.locales).map(lk => {
                return {
                    value: lk,
                    label: this.$vuetify.lang.t(`$vuetify.${lk}`)
                }
            })
            this.locales = _.sortBy(locales, (l) => l.value)
            this.updateSettings()

            // directly check invitations for new registered user with invitation
            if (this.loggedIn) this.checkInvitations()

            if (this.$refs && this.$refs.tabs) this.$refs.tabs.callSlider()
        },
        beforeDestroy() {
            this.clearReloadInterval()
            this.$visibility.unbind(this.visibilityHandler) // this.visibilityHandler is a number, could be 0
        },

        methods: {
            checkRoute() {
                if (!this.loggedIn) return

                // TODO check route coaching and push to a loading screen?
                // TODO prevent loading all org data, only needed?
                const groups = _.get(this.$route, 'meta.groups')
                if (groups && !groups.includes(this.userGroup)) {
                    this.$router.push({name: 'home'})
                }
            },

            // refresh state
            // - user invitations
            // - org fetch: refresh all data from current organizations
            reload() {
                this.clearReloadInterval()
                if (!this.loggedIn) return

                const _reload = async () => {
                    const val = await this.$store.dispatch('auth/validate')
                    if (!_.get(val, 'user.userId')) {
                        this.clearReloadInterval()
                        return
                    }
                    if (this.hidden) return // no data refresh when window is hidden

                    await this.$store.dispatch('api/USERS_INVITATIONS')
                    if (this.autoReload) {
                        const organisationId = _.get(this.organisation, 'organisationId')
                        // check fetching active again to prevent multiple fetches
                        if (organisationId && !this.$store.getters['api/fetching'].active) {
                            await this.$store.dispatch('api/ORG_FETCH', {
                                organisationId,
                                autoReload: true
                            })
                        }
                    }
                }

                // fire initial reload if not already fetching, then activate interval
                if (!this.$store.getters['api/fetching'].active) _reload()
                // start reload again after defined interval
                this.intervalId = window.setInterval(_reload, CONSTANTS.RELOAD_INTERVAL)
            },
            checkInvitations() {
                this.$store.dispatch('api/USERS_INVITATIONS').then(invitations => {
                    this.messagesHint = !_.isEmpty(invitations)
                    if (_.isEmpty(this.organisation) && this.messagesHint) {
                        this.drawerMessages = true
                    }
                })
            },
            clearReloadInterval() {
                if (!this.intervalId) return
                window.clearInterval(this.intervalId)
                this.intervalId = null
            },

            // update app ui settings
            // NOTE userSettings could be undefined on route login, landing
            updateSettings() {
                const userSettings = _.get(this.currentUser, 'settings')

                // theme (dark or light)
                const theme = _.get(userSettings, 'theme', CONSTANTS.DEFAULT_SETTINGS.theme)
                this.dark = theme.toLowerCase() === 'dark'

                // language (de or en)
                const language = _.get(userSettings, 'language', CONSTANTS.DEFAULT_SETTINGS.language)
                this.lang = language === 'German' ? 'de' : 'en'
                this.$vuetify.lang.current = this.lang
                moment.locale(this.lang)
                numeral.locale(this.lang === 'en' ? 'en-gb' : 'de')
                this.formattypes = ['iso', 'de', 'en'].map(value => {
                    return {
                        value,
                        label: this.$vuetify.lang.t(`$vuetify.formattypes.${value}`)
                    }
                })
                const dateFormat = _.get(userSettings, 'dateFormat', CONSTANTS.DEFAULT_SETTINGS.dateFormat)
                this.$store.dispatch('app/formatter', dateFormat)

                // dense view
                this.dense = _.get(userSettings, 'denseMode', CONSTANTS.DEFAULT_SETTINGS.dense)

                // check for new changes
                this.updateChangelogs()
            },
            // check for new changes
            updateChangelogs() {
                const lastVersionChangelogs = _.get(this.currentUser, 'settings.lastVersionChangeLogs')
                const lastChange = _.first(this.changelogs)
                const lastVersionCurrent = lastChange.version
                this.hasChanges = lastVersionChangelogs !== lastVersionCurrent
                if (this.hasChanges) {
                    this.hintChanges = [`Version ${lastVersionCurrent}`]
                    const lastVersionHint = lastChange.hint || ''
                    if (lastVersionHint) this.hintChanges.push(`${lastVersionHint}`)
                }
            },
            updateMessages() {
                this.userInvitations.forEach(async invite => {
                    // check if message already exists
                    const exists = _.find(this.messages, {id: invite.actionTokenId})
                    if (!exists) {
                        const metadata = $.parseJSON(invite.metadata)
                        const organisationId = _.get(metadata, 'OrganisationId')
                        if (organisationId) {
                            const time = moment().format('HH:mm')
                            const tagtext = moment(invite.created).format('YYYY-MM-DD HH:mm')
                            const groupName = this.$vuetify.lang.t(`$vuetify.groups.${metadata.OrganizationGroup}`)
                            const expired = moment(invite.expireDate).isBefore(moment())
                            const color = expired ? 'red' : 'green'
                            const tagcolor = expired ? 'red' : 'green'
                            const action = expired ? '' : this.$vuetify.lang.t('$vuetify.messages.acceptInvitation')
                            const msg = {
                                id: invite.actionTokenId,
                                type: invite.type,
                                text: `[ ${metadata.OrganizationInitials} ] ${metadata.OrganizationName}`,
                                description: `${this.$vuetify.lang.t('$vuetify.entity.group')}: ${groupName}`,
                                time,
                                color,
                                tag: this.$vuetify.lang.t('$vuetify.entity.invitation'),
                                tagcolor,
                                tagtext,
                                action
                            }
                            await this.$store.dispatch('app/addmessage', msg)
                        }
                    }
                })
            },
            openPwDialog() {
                this.drawer = false
                this.pwdialog = true
            },
            changePassword(fd) {
                this.pwdialog = false

                const userId = _.get(this.currentUser, 'user.userId')
                const oldPassword = fd['vpw-old']
                const newPassword = fd['vpw-new']
                // set new password
                this.$store.dispatch('api/USERS_RESET_PW', {userId, oldPassword, newPassword}).then(success => {
                    if (success) {
                        this.$toast.open({
                            text: this.$vuetify.lang.t('$vuetify.password.changed')
                        })
                    }
                })
            },
            logout() {
                this.clearReloadInterval()
                this.menu = false
                this.$store.dispatch('auth/logOut')
            },
            async organisationSelected(org) {
                const settings = {
                    userId: this.currentUser.user.userId,
                    lastOrganisationId: org.organisationId
                }
                await this.$store.dispatch('api/USERS_SETTINGS', settings)
                await this.$store.dispatch('api/ORG_ACTIVE', org)
            },
            showChanges() {
                this.right = !this.right
            },
            showMessages() {
                this.drawerMessages = !this.drawerMessages
            },
            startTour() {
                this.$tours.intro.start()
            },
            handleMessageAction(message) {
                if (message.type === 'OrganizationInvitation') {
                    const actionTokenId = message.id
                    this.$store.dispatch('api/USERS_ACCEPT_INVITATION', {actionTokenId}).then(success => {
                        if (success === 'invalid') { /* TODO show message? */
                        }
                        if (success) {
                            this.$store.dispatch('app/REMOVE_MESSAGE', {id: message.id}).then(() => {
                                if (_.isEmpty(this.messages)) {
                                    _.delay(() => {
                                        this.drawerMessages = false
                                    }, 1200)
                                }
                            })
                        }
                    })
                }
            },
            openAdminPage() {
                this.$router.push({ name: 'administration' })
            },
            openSettings() {
                this.menu = false
                this.showSettings = true
            },
            translateConstants() {
                // translate error messages
                CONSTANTS.ERRORS.title = this.$vuetify.lang.t('$vuetify.errors.title')
                CONSTANTS.ERRORS.defaultMessage = this.$vuetify.lang.t('$vuetify.errors.defaultMessage')
                CONSTANTS.ERRORS.status403Message = this.$vuetify.lang.t('$vuetify.errors.status403Message')

                // translate default fieldlist name and description for grouping
                CONSTANTS.DEFAULT_FIELDLIST.name = this.$vuetify.lang.t('$vuetify.gfb.block.defaultName')
                CONSTANTS.DEFAULT_FIELDLIST.description = this.$vuetify.lang.t('$vuetify.gfb.block.defaultDescription')
            },
            openTutorial(tutorial) {
                if (!tutorial || !tutorial.href) return
                window.open(tutorial.href, '_blank', 'noopener,noreferrer')
            }
        }
    }
</script>

<template>
    <VApp :class="appClass">
        <VAppBar v-if="!loadingScreen" app dense :extended="loggedIn && $vuetify.breakpoint.xs">
            <img class="topbar-image" src="@src/assets/images/top-bar-background.svg" width="314" height="48">
            <VAppBarNavIcon v-if="loggedIn" id="toolbar-side-icon-left" @click.stop="drawer = !drawer"/>
            <RouterLink to="home" class="app-logo">
                <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="30 24 250.58 80" xml:space="preserve"
                     fill="#ff3c00">
                    <path class="st0" d="M82.71,59.77c0.41,0,0.61,0.31,0.46,0.68l-9.88,23.85c-0.16,0.38-0.61,0.68-1.02,0.68H60.91c0,0,0,0,0,0h-2.54
            c-0.41,0-0.86-0.3-1.02-0.68L40.34,43.15c-0.16-0.38,0.05-0.68,0.46-0.68h11.41c0.41,0,0.87,0.3,1.02,0.68l12.11,29.33l4.96-12.03
            c0.16-0.38,0.61-0.68,1.02-0.68H82.71 M89.87,42.47h-11.4c-0.41,0-0.87,0.31-1.02,0.68l-3.52,8.6c-0.13,0.32,0.04,0.57,0.38,0.57
            h11.62c0.34,0,0.72-0.26,0.85-0.57l3.54-8.6C90.48,42.78,90.28,42.47,89.87,42.47"/>
                    <path class="st0" d="M134.96,75.19c-0.34,0-0.62-0.28-0.62-0.62v-5.8c0-0.34,0.28-0.62,0.62-0.62h17.81c0.34,0,0.62-0.28,0.62-0.62
            v-7.1c0-0.34-0.28-0.62-0.62-0.62h-17.81c-0.34,0-0.62-0.28-0.62-0.62v-6.27c0-0.34,0.28-0.62,0.62-0.62h20.51
            c0.34,0,0.62-0.28,0.62-0.62v-8.6c0-0.34-0.28-0.62-0.62-0.62H123.2c-0.34,0-0.62,0.28-0.62,0.62v41.29c0,0.34,0.28,0.62,0.62,0.62
            h34.22c0.34,0,0.62-0.28,0.62-0.62V75.8c0-0.34-0.27-0.62-0.62-0.62H134.96z"/>
                    <path class="st0" d="M98.5,43.04v41.38c0,0.31,0.25,0.57,0.57,0.57h10.63c0.31,0,0.57-0.25,0.57-0.57V43.04
            c0-0.31-0.25-0.57-0.57-0.57H99.07C98.76,42.47,98.5,42.73,98.5,43.04z"/>
                    <path class="st0" d="M209.96,84.37c0.16,0.22,0.05,0.62-0.34,0.62h-10.99c-0.46,0-0.81-0.24-1.1-0.61l-9.35-11.92
            c-0.21-0.27-0.66-0.49-1-0.49l-5.98,0c-0.34,0-0.62,0.28-0.62,0.62v11.73c0,0.34-0.27,0.62-0.62,0.62h-10.6
            c-0.34,0-0.62-0.28-0.62-0.62V43.09c0-0.34,0.28-0.62,0.62-0.62c0,0,17.57,0,23.43,0l0.01,0.01c8,0.26,15.08,6.83,15,15l0.02,0.02
            c0,5.97-2.8,11.1-7.5,13.27c-0.14,0.07-0.54,0.25-0.54,0.25L209.96,84.37z M196.4,57.83c0-3.74-1.96-5.5-5.7-5.5h-9.5
            c-0.34,0-0.62,0.28-0.62,0.62l0,9.62c0,0.34,0.28,0.62,0.62,0.62h9.5C194.43,63.18,196.4,61.57,196.4,57.83"/>
                </svg>
            </RouterLink>
            <VAppBarTitle v-if="$vuetify.breakpoint.lgAndUp">Scorecards</VAppBarTitle>

            <!-- Tabs on top on large screens -->
            <VTabs v-if="loggedIn && !$vuetify.breakpoint.xs"
                   ref="tabs"
                   centered
                   background-color="transparent">
                <VTooltip v-for="tab in availableRoutes" :key="tab.id"
                    :disabled="!$vuetify.breakpoint.smAndDown"
                    bottom
                    :open-delay="500">
                    <template v-slot:activator="{ on }">
                        <VTab ripple :to="tab.to" :disabled="fetchingChange" v-on="on">
                            <VIcon v-if="$vuetify.breakpoint.mdAndDown">{{ tab.icon }}</VIcon>
                            <span v-if="!$vuetify.breakpoint.mdAndDown">{{ tab.label }}</span>
                        </VTab>
                    </template>
                    <span>{{ tab.label }}</span>
                </VTooltip>
            </VTabs>
            <!-- Tabs in extension of smaller screens -->
            <template v-if="loggedIn && $vuetify.breakpoint.xs" v-slot:extension>
                <VTabs class="v-slide-group__prev--hidden">
                    <VTooltip v-for="tab in routes" :key="tab.id" :disabled="!$vuetify.breakpoint.mdAndDown" bottom
                              :open-delay="500">
                        <template v-slot:activator="{ on }">
                            <VTab ripple :to="tab.to" v-on="on">
                                <VIcon>{{ tab.icon }}</VIcon>
                            </VTab>
                        </template>
                        <span>{{ tab.label }}</span>
                    </VTooltip>
                </VTabs>
            </template>

            <VSpacer/>

            <VRadioGroup v-if="showLanguageSelection" v-model="lang" class="ml-3" :dense="dense" hide-details row>
                <VRadio v-for="n in locales" :key="n.value" :label="`${n.label}`" :value="n.value"/>
            </VRadioGroup>
            <VSpacer v-if="showLanguageSelection"/>

            <VTooltip v-if="loggedIn && tour.length > 0" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn icon v-on="on" @click="startTour">
                        <VIcon>mdi-map-marker-alert-outline</VIcon>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.settings.startTour') }}</span>
            </VTooltip>

            <VTooltip v-if="!loggedIn || (loggedIn && hasChanges)" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn id="toolbar-has-changes" key="new" icon v-on="on" @click.left="showChanges">
                        <VIcon large color="accent" class="animated tada delay-2s">mdi-new-box</VIcon>
                    </VBtn>
                </template>
                <div v-for="(hint, index) in hintChanges" :key="index" v-text="hint"></div>
            </VTooltip>

            <VTooltip v-if="messagesHint" bottom>
                <template v-slot:activator="{ on }">
                    <VBtn id="toolbar-has-messages" key="messages" icon v-on="on" @click="showMessages">
                        <VBadge :value="messagesCount" color="accent" :content="formatMessagesCount" dot>
                            <VIcon class="animated wobble delay-2s">mdi-message</VIcon>
                        </VBadge>
                    </VBtn>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.settings.showMessages') }} ({{ formatMessagesCount }})</span>
            </VTooltip>

            <!-- Login Route -->
            <VBtn
                v-if="showLoginRoute"
                id="vxc-to-login"
                key="login"
                text
                outlined
                :small="!$vuetify.breakpoint.mdAndUp"
                :to="`/login?lang=${lang}`"
            >
                <VIcon left>mdi-account-box</VIcon>
                {{ $vuetify.lang.t('$vuetify.login.label') }}
            </VBtn>
            <!-- Landing Route -->
            <VBtn
                v-if="showRegisterRoute"
                key="register"
                text
                outlined
                :small="!$vuetify.breakpoint.mdAndUp"
                to="/"
            >
                <VIcon left>mdi-account-box</VIcon>
                {{ $vuetify.lang.t('$vuetify.register.label') }}
            </VBtn>

            <VSpacer v-if="loggedIn"/>
            <VMenu v-if="loggedIn" v-model="menu" :close-on-content-click="false">
                <template v-slot:activator="{ on }">
                    <VBtn icon v-on="on">
                        <VIcon large v-text="currentUserMenuIcon"/>
                    </VBtn>
                    <h4 v-if="currentUser" class="subtitle text-no-wrap d-none d-lg-flex mx-2" v-on="on">
                        {{ currentUserName }}</h4>
                </template>

                <VCard min-width="300" :max-height="menuMaxHeight">
                    <VSubheader>{{ $vuetify.lang.t('$vuetify.user.loggedInAs') }}</VSubheader>
                    <VDivider/>
                    <VSheet class="px-4 my-4">
                        <div v-if="currentUser" class="title">{{ currentUserName }}</div>
                        <div v-if="currentUser" class="subtitle-2">{{ currentUserEmail }}</div>
                        <div v-if="currentUser" class="subtitle-2">{{ currentUserGroup }}</div>
                    </VSheet>

                    <VDivider/>

                    <VList class="vx-list pa-0">
                        <VListItem @click.left="openSettings">
                            <VListItemIcon>
                                <VIcon>mdi-account-cog-outline</VIcon>
                            </VListItemIcon>
                            <VListItemContent>
                                <VListItemTitle>{{ $vuetify.lang.t('$vuetify.user.settingsMore') }}</VListItemTitle>
                            </VListItemContent>
                        </VListItem>
                    </VList>

                    <VDivider/>

                    <VSubheader>{{ $vuetify.lang.t('$vuetify.locales.selection') }}</VSubheader>
                    <VDivider/>
                    <VSheet class="px-4 mb-4">
                        <VRadioGroup v-model="lang" hide-details>
                            <VRadio v-for="n in locales" :key="n.value" :label="`${n.label}`" :value="n.value"/>
                        </VRadioGroup>
                    </VSheet>

                    <VSubheader>{{ $vuetify.lang.t('$vuetify.user.settings') }}</VSubheader>
                    <VDivider/>

                    <VList class="vx-list pa-0">
                        <VListItem v-if="experimentalMode">
                            <VListItemAction>
                                <VSwitch v-model="hints" hide-details/>
                            </VListItemAction>
                            <VListItemTitle>{{ $vuetify.lang.t('$vuetify.options.hints') }}</VListItemTitle>
                        </VListItem>

                        <VListItem>
                            <VListItemAction>
                                <VSwitch v-model="autoReload" hide-details dense/>
                            </VListItemAction>
                            <VListItemTitle style="cursor:pointer" @click.left="autoReload = !autoReload">
                                {{ $vuetify.lang.t('$vuetify.options.autoReload') }}
                            </VListItemTitle>
                        </VListItem>

                        <VListItem>
                            <VListItemAction>
                                <VSwitch v-model="dense" hide-details dense/>
                            </VListItemAction>
                            <VListItemTitle style="cursor:pointer" @click.left="dense = !dense">
                                {{ $vuetify.lang.t('$vuetify.options.dense') }}
                            </VListItemTitle>
                        </VListItem>

                        <VListItem>
                            <VListItemAction>
                                <VSwitch v-model="dark" hide-details dense/>
                            </VListItemAction>
                            <VListItemTitle style="cursor:pointer" @click.left="dark = !dark">
                                {{ $vuetify.lang.t('$vuetify.options.theme') }}
                            </VListItemTitle>
                        </VListItem>
                    </VList>

                    <VDivider/>

                    <VList class="vx-list pa-0">
                        <VListItem @click.left="logout">
                            <VListItemIcon>
                                <VIcon>mdi-logout</VIcon>
                            </VListItemIcon>
                            <VListItemContent>
                                <VListItemTitle>{{ $vuetify.lang.t('$vuetify.actions.logout') }}</VListItemTitle>
                            </VListItemContent>
                        </VListItem>
                    </VList>
                </VCard>
            </VMenu>
        </VAppBar>
        <CoachingTeaser v-else fullsize/>

        <VMain class="vbp-main" transition="slide-x-transition">
            <LoadingIndicator v-if="fetchingChange"/>
            <VContainer fluid scrollable fill-height pa-0>
                <!--
                RouterView template root element should be a <VLayout><VFlex></VFlex></VLayout>
                Even when routes use the same component, treat them
                as distinct and create the component again.
                -->
                <RouterView :key="$route.fullPath"/>
            </VContainer>
        </VMain>

        <!-- App Sidebar -->
        <VNavigationDrawer v-model="drawer" absolute temporary width="320">
            <VSheet class="v-footer" tile>
                <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 20 250.58 80" xml:space="preserve"
                     fill="#ff3c00">
                    <path class="st0" d="M82.71,59.77c0.41,0,0.61,0.31,0.46,0.68l-9.88,23.85c-0.16,0.38-0.61,0.68-1.02,0.68H60.91c0,0,0,0,0,0h-2.54
            c-0.41,0-0.86-0.3-1.02-0.68L40.34,43.15c-0.16-0.38,0.05-0.68,0.46-0.68h11.41c0.41,0,0.87,0.3,1.02,0.68l12.11,29.33l4.96-12.03
            c0.16-0.38,0.61-0.68,1.02-0.68H82.71 M89.87,42.47h-11.4c-0.41,0-0.87,0.31-1.02,0.68l-3.52,8.6c-0.13,0.32,0.04,0.57,0.38,0.57
            h11.62c0.34,0,0.72-0.26,0.85-0.57l3.54-8.6C90.48,42.78,90.28,42.47,89.87,42.47"/>
                    <path class="st0" d="M134.96,75.19c-0.34,0-0.62-0.28-0.62-0.62v-5.8c0-0.34,0.28-0.62,0.62-0.62h17.81c0.34,0,0.62-0.28,0.62-0.62
            v-7.1c0-0.34-0.28-0.62-0.62-0.62h-17.81c-0.34,0-0.62-0.28-0.62-0.62v-6.27c0-0.34,0.28-0.62,0.62-0.62h20.51
            c0.34,0,0.62-0.28,0.62-0.62v-8.6c0-0.34-0.28-0.62-0.62-0.62H123.2c-0.34,0-0.62,0.28-0.62,0.62v41.29c0,0.34,0.28,0.62,0.62,0.62
            h34.22c0.34,0,0.62-0.28,0.62-0.62V75.8c0-0.34-0.27-0.62-0.62-0.62H134.96z"/>
                    <path class="st0" d="M98.5,43.04v41.38c0,0.31,0.25,0.57,0.57,0.57h10.63c0.31,0,0.57-0.25,0.57-0.57V43.04
            c0-0.31-0.25-0.57-0.57-0.57H99.07C98.76,42.47,98.5,42.73,98.5,43.04z"/>
                    <path class="st0" d="M209.96,84.37c0.16,0.22,0.05,0.62-0.34,0.62h-10.99c-0.46,0-0.81-0.24-1.1-0.61l-9.35-11.92
            c-0.21-0.27-0.66-0.49-1-0.49l-5.98,0c-0.34,0-0.62,0.28-0.62,0.62v11.73c0,0.34-0.27,0.62-0.62,0.62h-10.6
            c-0.34,0-0.62-0.28-0.62-0.62V43.09c0-0.34,0.28-0.62,0.62-0.62c0,0,17.57,0,23.43,0l0.01,0.01c8,0.26,15.08,6.83,15,15l0.02,0.02
            c0,5.97-2.8,11.1-7.5,13.27c-0.14,0.07-0.54,0.25-0.54,0.25L209.96,84.37z M196.4,57.83c0-3.74-1.96-5.5-5.7-5.5h-9.5
            c-0.34,0-0.62,0.28-0.62,0.62l0,9.62c0,0.34,0.28,0.62,0.62,0.62h9.5C194.43,63.18,196.4,61.57,196.4,57.83"/>
                </svg>
            </VSheet>

            <VList :dense="dense">
                <VListItem link @click.left="openPwDialog">
                    <VListItemIcon>
                        <VIcon>mdi-lock</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ $vuetify.lang.t('$vuetify.drawer.changePassword') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VListItem link @click.left="right = !right">
                    <VListItemIcon>
                        <VIcon>mdi-new-box</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ $vuetify.lang.t('$vuetify.drawer.changelog') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VListItem link href="mailto:support@vier.ai" target="_blank" rel="noopener noreferrer">
                    <VListItemIcon>
                        <VIcon>mdi-email-edit-outline</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ $vuetify.lang.t('$vuetify.drawer.feedback') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VListItem @click.left="drawer = false; showTutorialDialog = true">
                    <VListItemIcon>
                        <VIcon>mdi-youtube</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>Tutorials</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VListItem v-if="experimentalMode" link @click.left="drawerCalendar = !drawerCalendar">
                    <VListItemIcon>
                        <VIcon>mdi-calendar</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle>{{ $vuetify.lang.t('$vuetify.drawer.calendar') }}</VListItemTitle>
                    </VListItemContent>
                </VListItem>
                <VDivider v-if="developerMode"/>
                <VListItem v-if="developerMode" link @click.left="openAdminPage">
                    <VListItemIcon>
                        <VIcon color="accent">mdi-shield-lock-outline</VIcon>
                    </VListItemIcon>
                    <VListItemContent>
                        <VListItemTitle class="accent--text">{{
                                $vuetify.lang.t('$vuetify.drawer.adminPage')
                            }}
                        </VListItemTitle>
                    </VListItemContent>
                </VListItem>
            </VList>

            <LinkFooter as-list :dense="dense" class="pa-0"/>
        </VNavigationDrawer>
        <!-- Changelogs -->
        <VNavigationDrawer v-model="right" right temporary fixed :width="drawerWidth">
            <ChangeLog :logs="changelogs"/>
        </VNavigationDrawer>
        <!-- Messages -->
        <VNavigationDrawer v-model="drawerMessages" right temporary fixed light :width="drawerWidth">
            <MessageLog :logs="messages" @action="handleMessageAction"/>
        </VNavigationDrawer>
        <!-- Calendar -->
        <VNavigationDrawer v-if="experimentalMode" v-model="drawerCalendar" right temporary fixed
                           width="calc(100% - 322px)">
            <CoachingCalendar/>
        </VNavigationDrawer>

        <VFooter v-if="loggedIn && !loadingScreen" app class="vxc-footer">
            <span v-if="!$vuetify.breakpoint.smAndDown" class="subtitle pr-2 vc-disable-selection"
                  :class="{'animated infinite pulse': fetching && fetching.active}">{{ title }}</span>
            <span v-if="!$vuetify.breakpoint.smAndDown"
                  class="subtitle-2 pt-1 vc-disable-selection">&#xa9; 2019-{{ currentYear }} {{ author }}</span>
            <VSpacer v-if="!$vuetify.breakpoint.smAndDown"/>
            <span class="subtitle pr-1 vc-disable-selection">{{ currentUserName }}</span>
            <VSpacer v-if="$vuetify.breakpoint.xs"/>
            <ButtonSelect v-if="loggedIn && organisations.length"
                v-model="organisation"
                :items="organisations"
                :header="$vuetify.lang.t('$vuetify.organisation.selectHeader')"
                @change="organisationSelected"
            />
        </VFooter>

        <PasswordDialog
            v-if="loggedIn"
            v-model="pwdialog"
            :title="$vuetify.lang.t('$vuetify.register.pwTitle')"
            confirm-old
            cancel
            @cancel="pwdialog = false"
            @submit="changePassword"
        />

        <UserSettingsDialog v-model="showSettings" @cancel="showSettings = false"/>

        <VDialog v-model="showTutorialDialog" max-width="400px" transition="scale-transition"
                 @click:outside="showTutorialDialog = false">
            <VCard>
                <VCardTitle>VIER Scorecards Tutorial</VCardTitle>
                <VList>
                    <VListItem v-for="tutorial in tutorials" :key="tutorial.label" @click.left="openTutorial(tutorial)">
                        <VListItemIcon>
                            <VIcon>mdi-open-in-new</VIcon>
                        </VListItemIcon>
                        <VListItemContent>
                            <VListItemTitle>{{ tutorial.label }}</VListItemTitle>
                            <VListItemSubtitle>Per Klick Video in neuem Tab abspielen</VListItemSubtitle>
                        </VListItemContent>
                    </VListItem>
                </VList>
            </VCard>
        </VDialog>
    </VApp>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
    // Allow element/type selectors, because this is global CSS.
    // stylelint-disable selector-max-type, selector-class-pattern

    // animations
    @import '../node_modules/animate.css/animate.css';

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    html {
        overflow: hidden !important;
    }

    // ===
    // Vendor
    // ===

    // Edge and other HOT FIXES
    .v-application {
        .v-app-bar-title__content {
            overflow: visible !important;
            text-overflow: unset !important;
        }

        .topbar-image {
            position: absolute;
            top: 0;
            right: 0;
            z-index: -1;
        }

        .v-sheet.elevation-2 {
            border-radius: 5px;
            -webkit-box-shadow: 2px 2px 6px 0 rgba(0, 0, 0, 0.45) !important;
            box-shadow: 2px 2px 6px 0 rgba(0, 0, 0, 0.45) !important;
        }

        .row {
            margin-top: 0;
            margin-bottom: 0;
        }

        th {
            white-space: nowrap;
        }

        .v-main__wrap {
            width: 100%;
            // shrink scrollable content viewport
            // prevent content under app fab
            margin-top: 32px;
            margin-bottom: 32px;
        }

        &.app-route-login,
        &.app-route-landing {
            .v-main__wrap {
                margin-top: 0;
                margin-bottom: 0;
            }
        }

        .row + .row {
            margin-top: 0;
        }

        // textarea with scrollbar and clear icon overlaps
        &.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot {
            margin-right: 0;
        }

        .v-tooltip__content {
            color: #fff;
            background-color: rgba(10, 0, 10, 0.95);
        }

        .app-logo {
            width: 114px;
        }
        .v-app-bar-title {
            overflow: visible;
            font-size: 18px;
            font-weight: 500;
        }

        .v-sheet.v-toolbar {
            border-radius: 4px 4px 0 0;
        }
        .v-data-table-header {
            background-color: rgba(128, 128, 128, 0.4);
        }
        .v-snack.v-toast .v-toast__content .subtitle-1 {
            font-weight: 500;
        }
        .v-menu__content > .theme--light.v-list {
            background-color: #f5f5f5;
        }

        .theme--light.v-btn.v-btn--outlined.v-btn--text {
            border-color: rgba(10, 0, 10, 0.87);
        }
        .v-btn .v-btn__content > .v-badge {
            display: inline-flex;
            align-items: center;
        }

        &.v-application--is-ltr .v-list-item__icon:first-child {
            margin-right: 16px;
        }
    }

    body,
    body .v-application,
    body .v-application .title,
    body .v-application .button,
    body .v-application .headline,
    body .v-application .caption,
    body .v-application .body-1,
    body .v-application .body-2,
    body .v-application .display-1,
    body .v-application .display-2,
    body .v-application .display-3,
    body .v-application .display-4,
    body .v-application .subtitle-1,
    body .v-application .subtitle-2,
    body .v-application .overline {
        font-family: DIN, Helvetica, Arial, sans-serif !important;
    }

    body .v-application {
        input, optgroup, select, textarea,
        .v-subheader,
        .v-card__subtitle,
        .v-card__text,
        .body-1,
        .caption {
            font-weight: 500;
        }

        button {
            font-weight: 700;
        }
    }

    .v-toolbar__content > a {
        height: 48px;
    }

    .v-toolbar__content > a > svg {
        width: 140px;
        height: 48px;
    }

    #toolbar-has-changes {
        margin: 0 16px;
    }

    .vc-monospace {
        font-family: Consolas, Monaco, 'Andale Mono', monospace !important;
    }

    .vc-disable-selection {
        pointer-events: none;
        user-select: none;
    }

    // make content scrollable
    .v-application--wrap {
        max-height: 100vh;
    }

    .app-bp-xs .v-app-bar,
    .app-bp-sm .v-app-bar {
        .v-tabs-slider-wrapper {
            width: 60px;
        }

        .v-tabs .v-tab {
            min-width: 60px;
        }
    }
    .app-bp-md .v-app-bar,
    .app-bp-lg .v-app-bar {
        .v-tabs-slider-wrapper {
            width: inherit;
        }

        .v-tabs .v-tab {
            min-width: 80px;
        }
    }
    .app-bp-xl .v-app-bar {
        .v-tabs-slider-wrapper {
            width: inherit;
        }

        .v-tabs .v-tab {
            min-width: 90px;
        }
    }

    .v-application .v-toolbar .v-tabs > .v-item-group {
        color: #ff3c00 !important;
        caret-color: #ff3c00 !important;
    }

    .v-application.is--fetching {
        .vc-stats-tile__body,
        .knob,
        .home__chart canvas {
            animation: grey-out 2s ease-in forwards;
        }
    }

    @keyframes grey-out {
        25% {
            filter: blur(1px) grayscale(.25);
        }
        50% {
            filter: blur(2px) grayscale(.5);
        }
        75% {
            filter: blur(3px) grayscale(.75);
        }
        100% {
            filter: blur(4px) grayscale(1);
        }
    }

    // Vuetify HOT FIXES
    .v-tabs--centered > .v-tabs-bar .v-tabs-bar__content > .v-tab:first-of-type {
        margin-left: auto;
    }

    .v-slide-group__prev--hidden .v-slide-group__prev {
        display: none !important;
    }

    // Vuetify sets since 2.2.20 margin-top in dense textfields
    // but not the input field
    .v-text-field.v-input--dense .v-input__append-inner .v-input__icon > .v-icon,
    .v-text-field.v-input--dense .v-input__prepend-inner .v-input__icon > .v-icon {
        margin-top: 0 !important;
    }

    // remove the box shadow in expanded table rows
    .vbp-main .v-data-table tbody tr.v-data-table__expanded__content {
        background-color: #F5F5F5;
        box-shadow: none;
    }

    .v-step {
        z-index: 999;
    }

    .vbp-main {
        height: 100%;

        .container.scrollable {
            overflow: auto;
        }
    }
    .v-application.theme--light .vbp-main {
        background: rgba(242, 242, 242, 1) linear-gradient(148deg, rgba(242, 242, 242, 1) 28%, rgba(227, 236, 243, 0.35) 99%);
    }

    .vxc-footer.theme--light {
        background-color: #e8e8e8 !important;
    }

    .v-tabs__div > a {
        font-weight: 600;
    }

    .v-data-table tr td .theme--light.v-btn:hover::before {
        opacity: 0.25;
    }

    .v-toolbar__content h4.subtitle {
        cursor: pointer;
    }

    .v-highlight {
        padding: 0 1px;
        font-size: 105%;
        font-weight: 500;
        color: #0a000a;
        background: #ff3c00;

        &.v-highlight--stopword {
            background: red;
        }
    }

    .vx-list .v-list-item__action,
    .vx-list .v-list-item__icon {
        min-width: 30px;
    }

    .v-data-table.v-data-table--dense td,
    .v-data-table.v-data-table--dense th {
        height: 36px;
        padding: 0 8px;
    }

    .position-relative {
        position: relative !important;
    }

    .visible-hidden {
        top: -10000px !important;
        left: -10000px !important;
    }

    /* styles ALL scrollbars */
    ::-webkit-scrollbar {
        position: relative;
        width: 8px;
        height: 8px;
    }

    ::-webkit-scrollbar-track {
        margin: 0;
        background: rgba(223, 223, 223, 0.6);
        // border: 1px solid rgba(223, 223, 223, 0.6);
    }

    ::-webkit-scrollbar-thumb {
        background-color: rgba(32, 32, 32, 0.4);
        background-clip: padding-box;
        border: 0;
        box-shadow: none;
    }

    .scrollable-x {
        overflow: auto hidden !important;
    }

    .scrollable-y {
        overflow: hidden auto !important;
    }

    .scrollable-both {
        overflow: auto !important;
    }

    :focus {
        outline-color: #ff3c00;
    }

    ::selection {
        color: #fff;
        background-color: #1c3257;
    }
</style>
